import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import LangToggle from "@/components/LangToggle";

export default function Login() {
  const { login, loading } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await login(form.email, form.password);
      toast.success("Welcome back");
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    }
  };

  return (
    <AuthLayout>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full">
        <div className="flex justify-end mb-4"><LangToggle /></div>
        <h1 className="font-display font-black text-4xl tracking-tighter mb-2">{t("login_title")}</h1>
        <p className="text-muted-foreground mb-8">{t("login_sub")}</p>
        <form onSubmit={submit} className="space-y-5" data-testid="login-form">
          <div>
            <Label htmlFor="email">{t("login_email")}</Label>
            <Input data-testid="login-email" id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
          </div>
          <div>
            <Label htmlFor="password">{t("login_password")}</Label>
            <Input data-testid="login-password" id="password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
          </div>
          <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full h-11 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white rounded-full">
            {loading ? "..." : t("login_submit")} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </form>

        {/* Demo login helper — remove for production */}
        <div className="mt-4 rounded-xl border border-dashed border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5 p-3 flex items-center justify-between gap-3">
          <div className="text-xs">
            <div className="font-semibold text-[hsl(var(--accent))]">Demo login</div>
            <div className="text-muted-foreground text-[11px]">admin@guardian360.com · Guardian@360Admin</div>
          </div>
          <Button
            type="button"
            data-testid="demo-fill"
            variant="outline"
            size="sm"
            onClick={() => setForm({ email: "admin@guardian360.com", password: "Guardian@360Admin" })}
          >
            Fill
          </Button>
        </div>
        <div className="text-sm text-muted-foreground mt-6">
          {t("login_switch")}{" "}
          <Link to="/register" className="text-[hsl(var(--primary))] font-medium hover:underline" data-testid="switch-to-register">
            {t("login_switch_link")}
          </Link>
        </div>
      </motion.div>
    </AuthLayout>
  );
}

export function AuthLayout({ children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1592711491694-e85f60a7773a?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/80 via-black/40 to-transparent" />
        <div className="relative z-10 p-12 flex flex-col h-full text-white">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-white grid place-items-center">
              <Shield className="w-5 h-5 text-[hsl(var(--primary))]" />
            </div>
            <div className="font-display font-black text-2xl tracking-tight">Guardian360</div>
          </Link>
          <div className="mt-auto">
            <div className="font-display font-black text-4xl xl:text-5xl tracking-tighter leading-tight">
              One scan.<br />Complete protection.
            </div>
            <p className="text-white/70 mt-4 max-w-md">The AI-powered safety network trusted by 12,000+ families worldwide.</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md">{children}</div>
      </div>
    </div>
  );
}
